// Local analytics fallback for when Google Analytics isn't available
// This stores basic usage statistics in localStorage

const LOCAL_ANALYTICS_KEY = 'datacalc_local_analytics';

const emptyAnalyticsData = () => ({
  framingChartDownloads: {
    total: 0,
    byCamera: {},
    byDate: {},
    sessions: []
  },
  calculationLogs: {
    total: 0,
    byCalculator: {},
    byDate: {},
    sessions: []
  },
  pageViews: {
    total: 0,
    byPage: {},
    sessions: []
  },
  firstVisit: new Date().toISOString(),
  lastUpdated: new Date().toISOString()
});

// Initialize local analytics storage
export const initializeLocalAnalytics = () => {
  const existing = localStorage.getItem(LOCAL_ANALYTICS_KEY);
  if (!existing) {
    localStorage.setItem(LOCAL_ANALYTICS_KEY, JSON.stringify(emptyAnalyticsData()));
  }
};

// Get current analytics data
export const getLocalAnalyticsData = () => {
  const data = localStorage.getItem(LOCAL_ANALYTICS_KEY);
  return data ? JSON.parse(data) : null;
};

// Update analytics data. Tracking calls can fire before
// initializeLocalAnalytics runs (analytics init is deferred to idle), so
// always merge over a complete skeleton instead of assuming stored shape.
const updateLocalAnalytics = (updateFunction) => {
  const current = { ...emptyAnalyticsData(), ...(getLocalAnalyticsData() || {}) };
  const updated = updateFunction(current);
  updated.lastUpdated = new Date().toISOString();
  localStorage.setItem(LOCAL_ANALYTICS_KEY, JSON.stringify(updated));
  return updated;
};

// Track framing chart download locally
export const trackLocalFramingChartDownload = (chartData) => {
  updateLocalAnalytics((data) => {
    const today = new Date().toISOString().split('T')[0];
    const cameraKey = `${chartData.brand} ${chartData.camera}`;
    
    // Update totals
    data.framingChartDownloads.total += 1;
    
    // Update by camera
    data.framingChartDownloads.byCamera[cameraKey] = 
      (data.framingChartDownloads.byCamera[cameraKey] || 0) + 1;
    
    // Update by date
    data.framingChartDownloads.byDate[today] = 
      (data.framingChartDownloads.byDate[today] || 0) + 1;
    
    // Add session record
    data.framingChartDownloads.sessions.push({
      timestamp: new Date().toISOString(),
      camera: cameraKey,
      resolution: chartData.resolution,
      aspectRatio: chartData.aspectRatio,
      safetyMargin: chartData.safetyMargin,
      isAnamorphic: parseFloat(chartData.lensSqueeze) !== 1
    });
    
    // Keep only last 1000 sessions to prevent storage bloat
    if (data.framingChartDownloads.sessions.length > 1000) {
      data.framingChartDownloads.sessions = data.framingChartDownloads.sessions.slice(-1000);
    }
    
    return data;
  });
  
  console.log('Local analytics: Tracked framing chart download');
};

// Track calculation log locally
export const trackLocalCalculationLog = (calculatorType, logData) => {
  updateLocalAnalytics((data) => {
    const today = new Date().toISOString().split('T')[0];
    
    // Update totals
    data.calculationLogs.total += 1;
    
    // Update by calculator
    data.calculationLogs.byCalculator[calculatorType] = 
      (data.calculationLogs.byCalculator[calculatorType] || 0) + 1;
    
    // Update by date
    data.calculationLogs.byDate[today] = 
      (data.calculationLogs.byDate[today] || 0) + 1;
    
    // Add session record
    data.calculationLogs.sessions.push({
      timestamp: new Date().toISOString(),
      calculatorType: calculatorType,
      logLength: logData.length,
      logPreview: logData.substring(0, 50) // First 50 chars for privacy
    });
    
    // Keep only last 1000 sessions
    if (data.calculationLogs.sessions.length > 1000) {
      data.calculationLogs.sessions = data.calculationLogs.sessions.slice(-1000);
    }
    
    return data;
  });
  
  console.log('Local analytics: Tracked calculation log');
};

// Track page view locally
export const trackLocalPageView = (pagePath, pageTitle) => {
  updateLocalAnalytics((data) => {
    const today = new Date().toISOString().split('T')[0];
    
    // Update totals
    data.pageViews.total += 1;
    
    // Update by page
    data.pageViews.byPage[pagePath] = 
      (data.pageViews.byPage[pagePath] || 0) + 1;
    
    // Add session record
    data.pageViews.sessions.push({
      timestamp: new Date().toISOString(),
      pagePath: pagePath,
      pageTitle: pageTitle
    });
    
    // Keep only last 1000 sessions
    if (data.pageViews.sessions.length > 1000) {
      data.pageViews.sessions = data.pageViews.sessions.slice(-1000);
    }
    
    return data;
  });
  
  console.log('Local analytics: Tracked page view', pagePath);
};

// Get analytics summary for dashboard
export const getLocalAnalyticsSummary = (days = 7) => {
  const data = getLocalAnalyticsData();
  if (!data) return null;
  
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  const cutoffISO = cutoffDate.toISOString();
  
  // Filter recent sessions
  const recentFramingDownloads = data.framingChartDownloads.sessions.filter(
    session => session.timestamp > cutoffISO
  );
  const recentCalculationLogs = data.calculationLogs.sessions.filter(
    session => session.timestamp > cutoffISO
  );
  
  // Calculate top cameras
  const cameraCount = {};
  recentFramingDownloads.forEach(session => {
    cameraCount[session.camera] = (cameraCount[session.camera] || 0) + 1;
  });
  const topCameras = Object.entries(cameraCount)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  
  // Calculate top calculators
  const calculatorCount = {};
  recentCalculationLogs.forEach(session => {
    calculatorCount[session.calculatorType] = (calculatorCount[session.calculatorType] || 0) + 1;
  });
  const topCalculators = Object.entries(calculatorCount)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
  
  return {
    framingChartDownloads: {
      total: recentFramingDownloads.length,
      byCamera: cameraCount,
      byDate: {}
    },
    calculationLogs: {
      total: recentCalculationLogs.length,
      byCalculator: calculatorCount,
      byDate: {}
    },
    topCameras: topCameras,
    topCalculators: topCalculators,
    totalSessions: data.framingChartDownloads.total + data.calculationLogs.total,
    firstVisit: data.firstVisit,
    lastUpdated: data.lastUpdated
  };
};

// Export analytics data (for backup or analysis)
export const exportLocalAnalytics = () => {
  const data = getLocalAnalyticsData();
  if (!data) return null;
  
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `datacalc-analytics-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  console.log('Local analytics data exported');
};

// Clear analytics data
export const clearLocalAnalytics = () => {
  localStorage.removeItem(LOCAL_ANALYTICS_KEY);
  initializeLocalAnalytics();
  console.log('Local analytics data cleared');
};

// Get usage statistics for display
export const getUsageStats = () => {
  const data = getLocalAnalyticsData();
  if (!data) return null;
  
  return {
    totalFramingCharts: data.framingChartDownloads.total,
    totalCalculations: data.calculationLogs.total,
    totalPageViews: data.pageViews.total,
    mostUsedCamera: Object.entries(data.framingChartDownloads.byCamera)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'None',
    mostUsedCalculator: Object.entries(data.calculationLogs.byCalculator)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'None',
    firstVisit: data.firstVisit,
    lastUpdated: data.lastUpdated
  };
}; 